/**
 * SubHexMap - Main component for the sub-hex map feature.
 * Provides a hexagonal sub-map within a parent hex that owners can paint.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type {
    SubHexData,
    SubHexViewport,
    SubHexPreviousState,
    SelectedTile,
    SubHexMapProps,
    SubHexListResponse,
    SubHexUpdateResponse,
    SubHexBulkUpdateResponse,
    SubHexBulkDeleteResponse,
    SubHexNeighborsResponse,
    NeighborHexData,
    PaintTool,
    SubHexLayerData,
    SubHexLayerCreateResponse,
    SubHexLayerUpdateResponse,
    SubHexLayerDeleteResponse,
    SubHexLoreEntry,
    SubHexLoreListResponse,
    SubHexLoreShowResponse,
    SubHexLoreStoreResponse,
} from './types';
import { SubHexMapCanvas } from './SubHexMapCanvas';
import { TilePalette } from './TilePalette';
import { LoreModal } from './LoreModal';
import { LoreTooltip } from './LoreTooltip';
import { hexKey, loreHexKey, getNeighborsOffset, getHexesInRadiusOffset, getHexLineOffset, isWithinBoundaryOffset, type AxialCoord } from '@/lib/hexMath';

// Zoom constraints
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 2.0;
const ZOOM_STEP = 0.1;

// Hex sizing constants (must match hexMath.ts)
const HEX_SIZE = 40;

// Layer constants
const MAX_LAYERS = 13;

// Wood panel styling for toolbar controls
const woodPanelStyle: React.CSSProperties = {
    background: 'linear-gradient(180deg, #8B6914 0%, #6B4423 50%, #5D3A1A 100%)',
    border: '2px solid #C9A227',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -1px 0 rgba(0,0,0,0.3), 0 3px 8px rgba(0,0,0,0.4)',
};

const woodPanelClass = "rounded-lg";

// Wood button styling (for individual buttons within panels)
const woodButtonClass = "p-2 rounded transition-all";
const woodButtonInactiveClass = "text-amber-100 hover:text-white hover:bg-amber-900/50";
const woodButtonActiveClass = "bg-amber-900/70 text-white shadow-inner";

// Active tool colors (keep distinct for usability)
const toolActiveColors: Record<string, string> = {
    brush: 'bg-purple-700 text-white',
    line: 'bg-purple-700 text-white',
    fill: 'bg-purple-700 text-white',
    erase: 'bg-red-700 text-white',
    lore: 'bg-amber-700 text-white',
};

/**
 * Calculate the zoom level needed to fit the entire hex map in the viewport.
 * Returns a zoom that shows the full map with some padding.
 * Uses dimensions for a REGULAR flat-top hex boundary (all edges equal length).
 */
function calculateFitZoom(mapRadius: number, containerWidth: number, containerHeight: number): number {
    // For a regular flat-top hex boundary:
    // - Circumradius (center to vertex) = 2 * mapRadius * HEX_SIZE
    // - Width = 2 * circumradius = 4 * mapRadius * HEX_SIZE
    // - Height = sqrt(3) * circumradius = 2 * sqrt(3) * mapRadius * HEX_SIZE
    const mapPixelWidth = 4 * mapRadius * HEX_SIZE;
    const mapPixelHeight = 2 * Math.sqrt(3) * mapRadius * HEX_SIZE;

    // Add padding (15% on each side)
    const paddingFactor = 0.85;

    // Calculate zoom to fit both dimensions
    const zoomX = (containerWidth * paddingFactor) / mapPixelWidth;
    const zoomY = (containerHeight * paddingFactor) / mapPixelHeight;

    // Use the smaller zoom to ensure the entire map fits
    const fitZoom = Math.min(zoomX, zoomY);

    // Clamp to valid zoom range
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, fitZoom));
}

/**
 * Create default viewport settings, optionally calculating fit zoom.
 */
function createDefaultViewport(mapRadius: number, containerWidth: number, containerHeight: number): SubHexViewport {
    return {
        offsetX: 0,
        offsetY: 0,
        zoom: calculateFitZoom(mapRadius, containerWidth, containerHeight),
    };
}

export function SubHexMap({ hexId, canEdit, className = '' }: SubHexMapProps) {
    // Sub-hex data stored as a Map for efficient lookup
    const [subHexes, setSubHexes] = useState<Map<string, SubHexData>>(new Map());
    const [mapRadius, setMapRadius] = useState(15);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Viewport state for pan/zoom - initialized after we know container size
    const [viewport, setViewport] = useState<SubHexViewport | null>(null);
    const [viewportInitialized, setViewportInitialized] = useState(false);

    // Selected tile for painting
    const [selectedTile, setSelectedTile] = useState<SelectedTile | null>(null);

    // Undo stack
    const [undoStack, setUndoStack] = useState<SubHexPreviousState[]>([]);

    // Container dimensions
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 800 });

    // Palette visibility
    const [showPalette, setShowPalette] = useState(false);

    // Mobile toolbar expanded state
    const [mobileToolsExpanded, setMobileToolsExpanded] = useState(false);

    // Paint tool mode (brush for single/continuous painting, fill for flood fill, line for line drawing)
    const [paintTool, setPaintTool] = useState<PaintTool>('brush');

    // Brush size (1-5): 1 = single hex, 2 = center + ring 1, etc.
    const [brushSize, setBrushSize] = useState(1);

    // Line tool state: start coordinate when drawing a line
    const [lineStart, setLineStart] = useState<AxialCoord | null>(null);

    // Hovered hex coordinate for display
    const [hoveredCoord, setHoveredCoord] = useState<AxialCoord | null>(null);

    // Painting state (mouse held down for continuous painting)
    const [isPainting, setIsPainting] = useState(false);
    // Track which hexes have been painted in current stroke to avoid duplicates
    const paintedInStrokeRef = useRef<Set<string>>(new Set());

    // Dragging state for pan
    const [isDragging, setIsDragging] = useState(false);
    const dragStart = useRef({ x: 0, y: 0 });
    const viewportStart = useRef({ x: 0, y: 0 });

    // Neighbor hex data for seamless map display
    const [neighbors, setNeighbors] = useState<NeighborHexData[]>([]);

    // Layer state
    const [layers, setLayers] = useState<SubHexLayerData[]>([]);
    const [currentLayer, setCurrentLayer] = useState<SubHexLayerData | null>(null);
    const [showLayerMenu, setShowLayerMenu] = useState(false);
    const [editingLayerName, setEditingLayerName] = useState<number | null>(null);
    const [newLayerName, setNewLayerName] = useState('');

    // Lore state
    const [loreEntries, setLoreEntries] = useState<Map<string, SubHexLoreEntry>>(new Map());
    const [showLoreModal, setShowLoreModal] = useState(false);
    const [loreModalCoord, setLoreModalCoord] = useState<{ layerId: number; q: number; r: number } | null>(null);
    const [loreModalContent, setLoreModalContent] = useState<string | null>(null);
    const [loreModalLoading, setLoreModalLoading] = useState(false);
    const [hoveredLore, setHoveredLore] = useState<{ lore: SubHexLoreEntry; x: number; y: number } | null>(null);

    // Fetch lore entries for the current hex (all layers)
    const fetchLoreEntries = useCallback(async () => {
        try {
            const response = await fetch(`/hex/${hexId}/lore`);
            if (!response.ok) return;

            const data: SubHexLoreListResponse = await response.json();
            const loreMap = new Map<string, SubHexLoreEntry>();
            data.loreEntries.forEach(entry => {
                // Key includes layerId to distinguish lore on different layers/z-levels
                loreMap.set(loreHexKey(entry.layerId, { q: entry.q, r: entry.r }), entry);
            });
            setLoreEntries(loreMap);
        } catch (err) {
            console.error('Failed to fetch lore entries:', err);
        }
    }, [hexId]);

    // Fetch sub-hexes for a specific layer
    const fetchSubHexes = useCallback(async (layerId?: number) => {
        const url = layerId
            ? `/hex/${hexId}/sub-hexes?layer_id=${layerId}`
            : `/hex/${hexId}/sub-hexes`;

        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch sub-hexes');

        const data: SubHexListResponse = await response.json();
        const hexMap = new Map<string, SubHexData>();
        data.subHexes.forEach(sh => {
            hexMap.set(hexKey({ q: sh.q, r: sh.r }), sh);
        });

        setSubHexes(hexMap);
        setMapRadius(data.mapRadius);
        setLayers(data.layers);
        setCurrentLayer(data.currentLayer);

        // Clear undo stack when switching layers
        if (layerId !== undefined) {
            setUndoStack([]);
        }

        return data;
    }, [hexId]);

    // Get layer ID from URL params
    const getLayerFromUrl = useCallback(() => {
        const params = new URLSearchParams(window.location.search);
        const layerParam = params.get('layer');
        return layerParam ? parseInt(layerParam, 10) : null;
    }, []);

    // Update URL with current layer (without page reload)
    const updateUrlWithLayer = useCallback((layerId: number) => {
        const url = new URL(window.location.href);
        url.searchParams.set('layer', layerId.toString());
        window.history.pushState({ layerId }, '', url.toString());
    }, []);

    // Fetch sub-hexes, neighbors, and lore on mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Check if there's a layer specified in URL params
                const urlLayerId = getLayerFromUrl();

                // Fetch main hex sub-hexes, neighbors, and lore in parallel
                const [subHexData, neighborsResponse] = await Promise.all([
                    fetchSubHexes(urlLayerId ?? undefined),
                    fetch(`/hex/${hexId}/sub-hexes/neighbors`),
                    fetchLoreEntries(),
                ]);

                // If no layer in URL, set it to the current layer
                if (!urlLayerId && subHexData.currentLayer) {
                    const url = new URL(window.location.href);
                    url.searchParams.set('layer', subHexData.currentLayer.id.toString());
                    window.history.replaceState({ layerId: subHexData.currentLayer.id }, '', url.toString());
                }

                // Fetch neighbors (optional - don't fail if this errors)
                if (neighborsResponse.ok) {
                    const neighborsData: SubHexNeighborsResponse = await neighborsResponse.json();
                    setNeighbors(neighborsData.neighbors);
                }

                setError(null);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load sub-hex map');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [hexId, fetchSubHexes, fetchLoreEntries, getLayerFromUrl]);

    // Switch to a different layer
    const switchLayer = useCallback(async (layerId: number, updateUrl = true) => {
        if (currentLayer?.id === layerId) return;

        try {
            setLoading(true);
            await fetchSubHexes(layerId);
            setShowLayerMenu(false);

            // Update URL with new layer (pushState for forward navigation)
            if (updateUrl) {
                updateUrlWithLayer(layerId);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to switch layer');
        } finally {
            setLoading(false);
        }
    }, [currentLayer?.id, fetchSubHexes, updateUrlWithLayer]);

    // Handle browser back/forward navigation
    useEffect(() => {
        const handlePopState = (event: PopStateEvent) => {
            const layerId = event.state?.layerId || getLayerFromUrl();
            if (layerId && layerId !== currentLayer?.id) {
                // Switch layer without updating URL (since we're responding to URL change)
                switchLayer(layerId, false);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [currentLayer?.id, getLayerFromUrl, switchLayer]);

    // Create a new layer
    const createLayer = useCallback(async (name: string) => {
        try {
            const response = await fetch(`/hex/${hexId}/layers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
                body: JSON.stringify({ name }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to create layer');
            }

            const data: SubHexLayerCreateResponse = await response.json();

            // Add to layers list
            setLayers(prev => [...prev, data.layer]);
            setNewLayerName('');

            // Switch to the new layer
            await switchLayer(data.layer.id);
        } catch (err) {
            console.error('Failed to create layer:', err);
            setError(err instanceof Error ? err.message : 'Failed to create layer');
        }
    }, [hexId, switchLayer]);

    // Rename a layer
    const renameLayer = useCallback(async (layerId: number, newName: string) => {
        try {
            const response = await fetch(`/hex/${hexId}/layers/${layerId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
                body: JSON.stringify({ name: newName }),
            });

            if (!response.ok) throw new Error('Failed to rename layer');

            const data: SubHexLayerUpdateResponse = await response.json();

            // Update layers list
            setLayers(prev => prev.map(l => l.id === layerId ? data.layer : l));

            // Update current layer if it was renamed
            if (currentLayer?.id === layerId) {
                setCurrentLayer(data.layer);
            }

            setEditingLayerName(null);
        } catch (err) {
            console.error('Failed to rename layer:', err);
        }
    }, [hexId, currentLayer?.id]);

    // Delete a layer
    const deleteLayer = useCallback(async (layerId: number) => {
        if (layers.length <= 1) {
            setError('Cannot delete the last layer');
            return;
        }

        try {
            const response = await fetch(`/hex/${hexId}/layers/${layerId}`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete layer');
            }

            const data: SubHexLayerDeleteResponse = await response.json();

            // Remove from layers list
            setLayers(prev => prev.filter(l => l.id !== data.deletedLayerId));

            // If the deleted layer was current, switch to first available layer
            if (currentLayer?.id === data.deletedLayerId) {
                const remainingLayers = layers.filter(l => l.id !== data.deletedLayerId);
                if (remainingLayers.length > 0) {
                    await switchLayer(remainingLayers[0].id);
                }
            }
        } catch (err) {
            console.error('Failed to delete layer:', err);
            setError(err instanceof Error ? err.message : 'Failed to delete layer');
        }
    }, [hexId, layers, currentLayer?.id, switchLayer]);

    // Set a layer as default
    const setDefaultLayer = useCallback(async (layerId: number) => {
        try {
            const response = await fetch(`/hex/${hexId}/layers/${layerId}/default`, {
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
            });

            if (!response.ok) throw new Error('Failed to set default layer');

            const data: SubHexLayerUpdateResponse = await response.json();

            // Update layers list - mark all as not default, except the one we just set
            setLayers(prev => prev.map(l => ({
                ...l,
                isDefault: l.id === layerId,
            })));

            // Update current layer if it's the one we just set as default
            if (currentLayer?.id === layerId) {
                setCurrentLayer(data.layer);
            }
        } catch (err) {
            console.error('Failed to set default layer:', err);
        }
    }, [hexId, currentLayer?.id]);

    // Move a layer up or down in the order
    const moveLayer = useCallback(async (layerId: number, direction: 'up' | 'down') => {
        const currentIndex = layers.findIndex(l => l.id === layerId);
        if (currentIndex === -1) return;

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex < 0 || newIndex >= layers.length) return;

        // Create new order array with swapped positions
        const newOrder = [...layers];
        [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];

        try {
            const response = await fetch(`/hex/${hexId}/layers/reorder`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
                body: JSON.stringify({
                    layerIds: newOrder.map(l => l.id),
                }),
            });

            if (!response.ok) throw new Error('Failed to reorder layers');

            // Update local state with new order
            setLayers(newOrder.map((l, idx) => ({ ...l, order: idx })));
        } catch (err) {
            console.error('Failed to reorder layers:', err);
        }
    }, [hexId, layers]);

    // Update dimensions on resize and initialize viewport to fit the map
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                const newWidth = containerRef.current.clientWidth;
                const newHeight = containerRef.current.clientHeight;
                setDimensions({ width: newWidth, height: newHeight });

                // Initialize viewport on first measurement (after data is loaded)
                if (!viewportInitialized && !loading && newWidth > 0 && newHeight > 0) {
                    setViewport(createDefaultViewport(mapRadius, newWidth, newHeight));
                    setViewportInitialized(true);
                }
            }
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, [loading, mapRadius, viewportInitialized]);

    // Paint a single tile at the given coordinate
    const paintTile = useCallback(
        async (coord: AxialCoord): Promise<boolean> => {
            if (!canEdit || !selectedTile || !currentLayer) return false;

            try {
                const response = await fetch(`/hex/${hexId}/sub-hexes`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                    },
                    body: JSON.stringify({
                        layer_id: currentLayer.id,
                        q: coord.q,
                        r: coord.r,
                        domain: selectedTile.domain,
                        tile_image: selectedTile.tileImage,
                    }),
                });

                if (!response.ok) throw new Error('Failed to update sub-hex');

                const data: SubHexUpdateResponse = await response.json();

                // Update local state
                setSubHexes(prev => {
                    const next = new Map(prev);
                    next.set(hexKey(coord), data.subHex);
                    return next;
                });

                // Add to undo stack
                setUndoStack(prev => [...prev, data.previousState]);
                return true;
            } catch (err) {
                console.error('Failed to paint tile:', err);
                return false;
            }
        },
        [hexId, canEdit, selectedTile, currentLayer]
    );

    // Paint multiple tiles in a single bulk request
    const paintTilesBulk = useCallback(
        async (coords: AxialCoord[]): Promise<boolean> => {
            if (!canEdit || !selectedTile || !currentLayer || coords.length === 0) return false;

            try {
                const response = await fetch(`/hex/${hexId}/sub-hexes/bulk`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                    },
                    body: JSON.stringify({
                        layer_id: currentLayer.id,
                        tiles: coords.map(c => ({ q: c.q, r: c.r })),
                        domain: selectedTile.domain,
                        tile_image: selectedTile.tileImage,
                    }),
                });

                if (!response.ok) throw new Error('Failed to bulk update sub-hexes');

                const data: SubHexBulkUpdateResponse = await response.json();

                // Update local state with all new hexes
                setSubHexes(prev => {
                    const next = new Map(prev);
                    for (const subHex of data.subHexes) {
                        next.set(hexKey({ q: subHex.q, r: subHex.r }), subHex);
                    }
                    return next;
                });

                // Add all previous states to undo stack
                setUndoStack(prev => [...prev, ...data.previousStates]);
                return true;
            } catch (err) {
                console.error('Failed to bulk paint tiles:', err);
                return false;
            }
        },
        [hexId, canEdit, selectedTile, currentLayer]
    );

    // Erase multiple tiles in a single bulk request
    const eraseTilesBulk = useCallback(
        async (coords: AxialCoord[]): Promise<boolean> => {
            if (!canEdit || !currentLayer || coords.length === 0) return false;

            // Filter to only include coordinates that actually have tiles
            const coordsWithTiles = coords.filter(c => subHexes.has(hexKey(c)));
            if (coordsWithTiles.length === 0) return false;

            try {
                const response = await fetch(`/hex/${hexId}/sub-hexes/bulk`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                    },
                    body: JSON.stringify({
                        layer_id: currentLayer.id,
                        tiles: coordsWithTiles.map(c => ({ q: c.q, r: c.r })),
                    }),
                });

                if (!response.ok) throw new Error('Failed to bulk delete sub-hexes');

                const data: SubHexBulkDeleteResponse = await response.json();

                // Update local state - remove deleted hexes
                setSubHexes(prev => {
                    const next = new Map(prev);
                    for (const coord of data.deletedCoords) {
                        next.delete(hexKey({ q: coord.q, r: coord.r }));
                    }
                    return next;
                });

                // Add all previous states to undo stack
                setUndoStack(prev => [...prev, ...data.previousStates]);
                return true;
            } catch (err) {
                console.error('Failed to bulk erase tiles:', err);
                return false;
            }
        },
        [hexId, canEdit, subHexes, currentLayer]
    );

    // Flood fill empty tiles starting from a coordinate
    const floodFill = useCallback(
        async (startCoord: AxialCoord) => {
            if (!canEdit || !selectedTile) return;

            // Check if starting tile is already filled
            const startKey = hexKey(startCoord);
            if (subHexes.has(startKey)) {
                // If clicking on a filled tile, just paint it (replace)
                await paintTile(startCoord);
                return;
            }

            // BFS to find all connected empty tiles
            const toFill: AxialCoord[] = [];
            const visited = new Set<string>();
            const queue: AxialCoord[] = [startCoord];

            while (queue.length > 0) {
                const current = queue.shift()!;
                const key = hexKey(current);

                // Skip if already visited
                if (visited.has(key)) continue;
                visited.add(key);

                // Skip if outside boundary
                if (!isWithinBoundaryOffset(current.q, current.r, mapRadius)) continue;

                // Skip if already has a tile (this is a boundary)
                if (subHexes.has(key)) continue;

                // This is an empty tile to fill
                toFill.push(current);

                // Add neighbors to queue (using offset coordinate system)
                const neighbors = getNeighborsOffset(current.q, current.r);
                for (const neighbor of neighbors) {
                    const neighborCoord: AxialCoord = { q: neighbor.col, r: neighbor.row };
                    const neighborKey = hexKey(neighborCoord);
                    if (!visited.has(neighborKey)) {
                        queue.push(neighborCoord);
                    }
                }
            }

            // Paint all tiles in a single bulk request
            if (toFill.length > 0) {
                await paintTilesBulk(toFill);
            }
        },
        [canEdit, selectedTile, subHexes, mapRadius, paintTile, paintTilesBulk]
    );

    // Paint all hexes in the brush area around a center coordinate
    const paintBrushArea = useCallback(
        async (centerCoord: AxialCoord) => {
            // Get hexes using offset coordinate system
            const offsetHexes = getHexesInRadiusOffset(centerCoord.q, centerCoord.r, brushSize);
            const hexesToPaint: AxialCoord[] = offsetHexes.map(h => ({ q: h.col, r: h.row }));

            // Paint each hex that's within the map boundary and not already painted in this stroke
            for (const hex of hexesToPaint) {
                if (!isWithinBoundaryOffset(hex.q, hex.r, mapRadius)) continue;

                const key = hexKey(hex);
                if (paintedInStrokeRef.current.has(key)) continue;

                paintedInStrokeRef.current.add(key);
                await paintTile(hex);
            }
        },
        [brushSize, mapRadius, paintTile]
    );

    // Erase all hexes in the brush area around a center coordinate
    const eraseBrushArea = useCallback(
        async (centerCoord: AxialCoord) => {
            // Get hexes using offset coordinate system
            const offsetHexes = getHexesInRadiusOffset(centerCoord.q, centerCoord.r, brushSize);
            const hexesToErase: AxialCoord[] = [];

            // Collect hexes that are within boundary, have tiles, and haven't been erased in this stroke
            for (const h of offsetHexes) {
                const hex: AxialCoord = { q: h.col, r: h.row };
                if (!isWithinBoundaryOffset(hex.q, hex.r, mapRadius)) continue;

                const key = hexKey(hex);
                if (paintedInStrokeRef.current.has(key)) continue;
                if (!subHexes.has(key)) continue; // Only erase tiles that exist

                paintedInStrokeRef.current.add(key);
                hexesToErase.push(hex);
            }

            // Erase all collected hexes in a single bulk request
            if (hexesToErase.length > 0) {
                await eraseTilesBulk(hexesToErase);
            }
        },
        [brushSize, mapRadius, subHexes, eraseTilesBulk]
    );

    // Paint all hexes along a line from start to end
    const paintLine = useCallback(
        async (start: AxialCoord, end: AxialCoord) => {
            // Get hexes using offset coordinate system
            const offsetHexes = getHexLineOffset(start.q, start.r, end.q, end.r);
            const hexesToPaint: AxialCoord[] = offsetHexes
                .map(h => ({ q: h.col, r: h.row }))
                .filter(hex => isWithinBoundaryOffset(hex.q, hex.r, mapRadius));

            // Paint all hexes in a single bulk request
            if (hexesToPaint.length > 0) {
                await paintTilesBulk(hexesToPaint);
            }
        },
        [mapRadius, paintTilesBulk]
    );

    // Handle hex click (paint tile, fill, erase, or start/end line)
    const handleHexClick = useCallback(
        async (coord: AxialCoord) => {
            if (!canEdit) return;

            // Erase tool doesn't require selectedTile
            if (paintTool === 'erase') {
                await eraseBrushArea(coord);
                return;
            }

            // All other tools require selectedTile
            if (!selectedTile) return;

            if (paintTool === 'fill') {
                await floodFill(coord);
            } else if (paintTool === 'line') {
                // Line tool: first click sets start, handled in handlePaintStart
                // This is called on mouse down, so we just set the start point
                setLineStart(coord);
            } else {
                // Brush tool: paint all hexes in the brush area
                await paintBrushArea(coord);
            }
        },
        [canEdit, selectedTile, paintTool, floodFill, paintBrushArea, eraseBrushArea]
    );

    // Handle continuous painting/erasing while mouse is held down
    const handlePaintMove = useCallback(
        async (coord: AxialCoord) => {
            if (!canEdit) return;

            // Erase tool - continuous erasing
            if (paintTool === 'erase') {
                await eraseBrushArea(coord);
                return;
            }

            // Brush tool - continuous painting (requires selectedTile)
            if (paintTool === 'brush' && selectedTile) {
                await paintBrushArea(coord);
            }
        },
        [canEdit, selectedTile, paintTool, paintBrushArea, eraseBrushArea]
    );

    // Handle paint start (mouse down)
    const handlePaintStart = useCallback(() => {
        setIsPainting(true);
        paintedInStrokeRef.current.clear();
    }, []);

    // Handle paint end (mouse up) - for line tool, this is when we paint
    const handlePaintEnd = useCallback(
        async (endCoord?: AxialCoord) => {
            // For line tool, paint the line on mouse up
            if (paintTool === 'line' && lineStart && endCoord) {
                await paintLine(lineStart, endCoord);
                setLineStart(null);
            }

            setIsPainting(false);
            paintedInStrokeRef.current.clear();
        },
        [paintTool, lineStart, paintLine]
    );

    // Handle undo
    const handleUndo = useCallback(async () => {
        if (undoStack.length === 0 || !currentLayer) return;

        const lastState = undoStack[undoStack.length - 1];

        // Only undo if the last action was on the current layer
        if (lastState.layerId !== currentLayer.id) return;

        try {
            const response = await fetch(`/hex/${hexId}/sub-hexes/undo`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
                body: JSON.stringify({
                    layer_id: lastState.layerId,
                    q: lastState.q,
                    r: lastState.r,
                    existed: lastState.existed,
                    domain: lastState.domain,
                    tileImage: lastState.tileImage,
                }),
            });

            if (!response.ok) throw new Error('Failed to undo');

            // Update local state
            setSubHexes(prev => {
                const next = new Map(prev);
                const key = hexKey({ q: lastState.q, r: lastState.r });

                if (lastState.existed && lastState.domain && lastState.tileImage) {
                    next.set(key, {
                        q: lastState.q,
                        r: lastState.r,
                        domain: lastState.domain,
                        tileImage: lastState.tileImage,
                    });
                } else {
                    next.delete(key);
                }
                return next;
            });

            // Remove from undo stack
            setUndoStack(prev => prev.slice(0, -1));
        } catch (err) {
            console.error('Failed to undo:', err);
        }
    }, [hexId, undoStack, currentLayer]);

    // Handle lore hover (for tooltip display)
    const handleLoreHover = useCallback((lore: SubHexLoreEntry | null, screenX: number, screenY: number) => {
        if (lore) {
            setHoveredLore({ lore, x: screenX, y: screenY });
        } else {
            setHoveredLore(null);
        }
    }, []);

    // Handle lore click (opens modal)
    const handleLoreClick = useCallback(async (coord: AxialCoord) => {
        if (!currentLayer) return;

        // Use layer-aware key to get lore for the current z-level only
        const key = loreHexKey(currentLayer.id, coord);
        const existingLore = loreEntries.get(key);

        setLoreModalCoord({ layerId: currentLayer.id, q: coord.q, r: coord.r });
        setLoreModalContent(null);
        setShowLoreModal(true);

        // If there's existing lore on this layer, fetch the full content
        if (existingLore) {
            setLoreModalLoading(true);
            try {
                const response = await fetch(`/hex/${hexId}/lore/${existingLore.layerId}/${coord.q}/${coord.r}`);
                if (response.ok) {
                    const data: SubHexLoreShowResponse = await response.json();
                    if (data.lore) {
                        setLoreModalContent(data.lore.content);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch lore content:', err);
            } finally {
                setLoreModalLoading(false);
            }
        }
    }, [hexId, currentLayer, loreEntries]);

    // Handle mobile double-tap navigation to library
    const handleLoreNavigate = useCallback(() => {
        // Navigate to the library tab for this hex
        window.location.href = `/hex/${hexId}?tab=library`;
    }, [hexId]);

    // Handle lore save
    const handleLoreSave = useCallback(async (title: string, content: string) => {
        if (!loreModalCoord) return;

        const response = await fetch(`/hex/${hexId}/lore`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
            },
            body: JSON.stringify({
                layerId: loreModalCoord.layerId,
                q: loreModalCoord.q,
                r: loreModalCoord.r,
                title,
                content,
            }),
        });

        if (!response.ok) throw new Error('Failed to save lore');

        const data: SubHexLoreStoreResponse = await response.json();

        // Update local lore entries with layer-aware key
        setLoreEntries(prev => {
            const next = new Map(prev);
            const key = loreHexKey(data.lore.layerId, { q: data.lore.q, r: data.lore.r });
            next.set(key, {
                layerId: data.lore.layerId,
                q: data.lore.q,
                r: data.lore.r,
                pageId: data.lore.pageId,
                title: data.lore.title,
            });
            return next;
        });

        setLoreModalContent(data.lore.content);
    }, [hexId, loreModalCoord]);

    // Handle lore delete
    const handleLoreDelete = useCallback(async () => {
        if (!loreModalCoord) return;

        // Use layer-aware key to find lore for this specific z-level
        const key = loreHexKey(loreModalCoord.layerId, { q: loreModalCoord.q, r: loreModalCoord.r });
        const lore = loreEntries.get(key);
        if (!lore) return;

        const response = await fetch(`/hex/${hexId}/lore/${lore.pageId}`, {
            method: 'DELETE',
            headers: {
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
            },
        });

        if (!response.ok) throw new Error('Failed to delete lore');

        // Remove from local state using layer-aware key
        setLoreEntries(prev => {
            const next = new Map(prev);
            next.delete(key);
            return next;
        });
    }, [hexId, loreModalCoord, loreEntries]);

    // Get current lore entry for modal using layer-aware key
    const currentLoreEntry = loreModalCoord
        ? loreEntries.get(loreHexKey(loreModalCoord.layerId, { q: loreModalCoord.q, r: loreModalCoord.r })) || null
        : null;

    // Zoom handlers
    const handleZoomIn = useCallback(() => {
        setViewport(prev => prev ? ({
            ...prev,
            zoom: Math.min(MAX_ZOOM, prev.zoom + ZOOM_STEP),
        }) : prev);
    }, []);

    const handleZoomOut = useCallback(() => {
        setViewport(prev => prev ? ({
            ...prev,
            zoom: Math.max(MIN_ZOOM, prev.zoom - ZOOM_STEP),
        }) : prev);
    }, []);

    // Reset view to fit the entire map
    const handleResetView = useCallback(() => {
        setViewport(createDefaultViewport(mapRadius, dimensions.width, dimensions.height));
    }, [mapRadius, dimensions.width, dimensions.height]);

    // Pan handlers - Mouse events
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        // Only start drag with left click and when not painting/erasing
        if (e.button === 0 && !selectedTile && paintTool !== 'erase' && viewport) {
            setIsDragging(true);
            dragStart.current = { x: e.clientX, y: e.clientY };
            viewportStart.current = { x: viewport.offsetX, y: viewport.offsetY };
        }
    }, [viewport, selectedTile, paintTool]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (isDragging) {
            const dx = e.clientX - dragStart.current.x;
            const dy = e.clientY - dragStart.current.y;
            setViewport(v => v ? ({
                ...v,
                offsetX: viewportStart.current.x + dx,
                offsetY: viewportStart.current.y + dy,
            }) : v);
        }
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Pinch-to-zoom state
    const pinchStartRef = useRef<{ distance: number; zoom: number; centerX: number; centerY: number } | null>(null);

    // Calculate distance between two touch points
    const getTouchDistance = (touches: React.TouchList) => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    // Get center point between two touches
    const getTouchCenter = (touches: React.TouchList) => ({
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2,
    });

    // Touch handlers for mobile pan and pinch-to-zoom
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        // Prevent page scrolling
        e.preventDefault();

        if (e.touches.length === 2 && viewport) {
            // Start pinch-to-zoom
            setIsDragging(false);
            pinchStartRef.current = {
                distance: getTouchDistance(e.touches),
                zoom: viewport.zoom,
                centerX: viewport.offsetX,
                centerY: viewport.offsetY,
            };
        } else if (e.touches.length === 1 && !selectedTile && paintTool !== 'erase' && viewport) {
            // Single touch - start panning
            setIsDragging(true);
            pinchStartRef.current = null;
            dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            viewportStart.current = { x: viewport.offsetX, y: viewport.offsetY };
        }
    }, [viewport, selectedTile, paintTool]);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        // Prevent page scrolling
        e.preventDefault();

        if (e.touches.length === 2 && pinchStartRef.current && viewport) {
            // Pinch-to-zoom
            const newDistance = getTouchDistance(e.touches);
            const scale = newDistance / pinchStartRef.current.distance;
            const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, pinchStartRef.current.zoom * scale));

            setViewport(v => v ? ({
                ...v,
                zoom: newZoom,
            }) : v);
        } else if (isDragging && e.touches.length === 1) {
            // Single touch - panning
            const dx = e.touches[0].clientX - dragStart.current.x;
            const dy = e.touches[0].clientY - dragStart.current.y;
            setViewport(v => v ? ({
                ...v,
                offsetX: viewportStart.current.x + dx,
                offsetY: viewportStart.current.y + dy,
            }) : v);
        }
    }, [isDragging, viewport]);

    const handleTouchEnd = useCallback((e: React.TouchEvent) => {
        // If still have touches, might be transitioning from pinch to pan
        if (e.touches.length === 1 && viewport) {
            // Transitioning from pinch to pan
            setIsDragging(true);
            pinchStartRef.current = null;
            dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            viewportStart.current = { x: viewport.offsetX, y: viewport.offsetY };
        } else if (e.touches.length === 0) {
            // All touches ended
            setIsDragging(false);
            pinchStartRef.current = null;
        }
    }, [viewport]);

    // Wheel zoom
    const handleWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        setViewport(prev => prev ? ({
            ...prev,
            zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev.zoom + delta)),
        }) : prev);
    }, []);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                e.preventDefault();
                handleUndo();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleUndo]);

    // Show loading state while data or viewport is being initialized
    if (loading || !viewport) {
        return (
            <div className={`flex items-center justify-center h-[calc(100vh-200px)] min-h-[600px] ${className}`}>
                <div
                    ref={containerRef}
                    className="w-full h-full flex items-center justify-center bg-slate-900 rounded-lg"
                >
                    <div className="text-gray-400">Loading sub-hex map...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`flex items-center justify-center h-[calc(100vh-200px)] min-h-[600px] ${className}`}>
                <div className="text-red-400">{error}</div>
            </div>
        );
    }

    return (
        <div className={`relative ${className}`}>
            {/* Map container - takes up most of the viewport height */}
            <div
                ref={containerRef}
                className={`relative w-full h-[calc(100vh-200px)] min-h-[600px] bg-slate-900 rounded-lg overflow-hidden touch-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <SubHexMapCanvas
                    subHexes={subHexes}
                    neighbors={neighbors}
                    mapRadius={mapRadius}
                    viewport={viewport}
                    width={dimensions.width}
                    height={dimensions.height}
                    selectedTile={selectedTile}
                    canEdit={canEdit}
                    isPainting={isPainting}
                    brushSize={brushSize}
                    paintTool={paintTool}
                    lineStart={lineStart}
                    loreEntries={loreEntries}
                    currentLayerId={currentLayer?.id ?? null}
                    onHexClick={handleHexClick}
                    onHexHover={setHoveredCoord}
                    onPaintStart={handlePaintStart}
                    onPaintEnd={handlePaintEnd}
                    onPaintMove={handlePaintMove}
                    onLoreHover={handleLoreHover}
                    onLoreClick={handleLoreClick}
                    onLoreNavigate={handleLoreNavigate}
                />

                {/* Lore Tooltip */}
                {hoveredLore && (
                    <LoreTooltip
                        title={hoveredLore.lore.title}
                        x={hoveredLore.x}
                        y={hoveredLore.y}
                    />
                )}

                {/* Controls overlay - Right side (zoom) */}
                <div className="absolute top-4 right-4 flex flex-col gap-2 z-20">
                    {/* Zoom controls */}
                    <div className={`${woodPanelClass} p-1 flex flex-col gap-1`} style={woodPanelStyle}>
                        <button
                            onClick={handleZoomIn}
                            className={`${woodButtonClass} ${woodButtonInactiveClass}`}
                            title="Zoom In"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12M6 12h12" />
                            </svg>
                        </button>
                        <button
                            onClick={handleZoomOut}
                            className={`${woodButtonClass} ${woodButtonInactiveClass}`}
                            title="Zoom Out"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12h12" />
                            </svg>
                        </button>
                        <button
                            onClick={handleResetView}
                            className={`${woodButtonClass} ${woodButtonInactiveClass}`}
                            title="Reset View"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </button>
                    </div>

                    {/* Undo button */}
                    {canEdit && undoStack.length > 0 && (
                        <button
                            onClick={handleUndo}
                            className={`${woodPanelClass} ${woodButtonClass} ${woodButtonInactiveClass}`}
                            style={woodPanelStyle}
                            title={`Undo (${undoStack.length})`}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* Left side controls - Layer selector and draw tools */}
                {/* Desktop: horizontal layout, Mobile: vertical compact layout */}
                <div className="absolute top-4 left-4 flex flex-col gap-2 z-20 max-w-[calc(100vw-120px)] md:max-w-none">
                    {/* Layer selector - always visible */}
                    {currentLayer && (
                        <div className="relative">
                            <button
                                onClick={() => setShowLayerMenu(!showLayerMenu)}
                                className={`${woodPanelClass} px-3 py-2 text-amber-100 hover:text-white transition-colors flex items-center gap-2 text-sm md:text-base`}
                                style={woodPanelStyle}
                            >
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                                <span className="font-medium truncate max-w-24 md:max-w-none">{currentLayer.name}</span>
                                {currentLayer.isDefault && (
                                    <span className="text-xs text-amber-300 hidden md:inline">(default)</span>
                                )}
                                <svg className={`w-4 h-4 flex-shrink-0 transition-transform ${showLayerMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {/* Layer dropdown menu */}
                            {showLayerMenu && (
                                <div
                                    className={`absolute top-full left-0 mt-1 ${woodPanelClass} shadow-xl overflow-hidden min-w-64 max-w-[calc(100vw-32px)]`}
                                    style={{
                                        background: 'linear-gradient(180deg, #6B4423 0%, #5D3A1A 50%, #4A2D14 100%)',
                                        border: '2px solid #C9A227',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                    }}
                                >
                                    {/* Layer list */}
                                    <div className="max-h-64 overflow-y-auto">
                                        {layers.map((layer) => (
                                            <div
                                                key={layer.id}
                                                className={`flex items-center gap-2 px-3 py-2 ${
                                                    currentLayer.id === layer.id
                                                        ? 'bg-amber-900/50'
                                                        : 'hover:bg-amber-900/30'
                                                }`}
                                            >
                                                {editingLayerName === layer.id ? (
                                                    <input
                                                        type="text"
                                                        defaultValue={layer.name}
                                                        autoFocus
                                                        className="flex-1 bg-amber-950 text-amber-100 px-2 py-1 rounded text-sm border border-amber-700"
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                renameLayer(layer.id, e.currentTarget.value);
                                                            } else if (e.key === 'Escape') {
                                                                setEditingLayerName(null);
                                                            }
                                                        }}
                                                        onBlur={(e) => {
                                                            renameLayer(layer.id, e.target.value);
                                                        }}
                                                    />
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={() => switchLayer(layer.id)}
                                                            className="flex-1 text-left text-amber-100 text-sm"
                                                        >
                                                            {layer.name}
                                                            {layer.isDefault && (
                                                                <span className="ml-2 text-xs text-amber-300">(default)</span>
                                                            )}
                                                        </button>
                                                        {canEdit && (
                                                            <div className="flex items-center gap-1">
                                                                {/* Move up button */}
                                                                {layers.length > 1 && layers.findIndex(l => l.id === layer.id) > 0 && (
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); moveLayer(layer.id, 'up'); }}
                                                                        className="p-1 text-amber-300 hover:text-white transition-colors"
                                                                        title="Move layer up"
                                                                    >
                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                                {/* Move down button */}
                                                                {layers.length > 1 && layers.findIndex(l => l.id === layer.id) < layers.length - 1 && (
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); moveLayer(layer.id, 'down'); }}
                                                                        className="p-1 text-amber-300 hover:text-white transition-colors"
                                                                        title="Move layer down"
                                                                    >
                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                                {!layer.isDefault && (
                                                                    <button
                                                                        onClick={() => setDefaultLayer(layer.id)}
                                                                        className="p-1 text-amber-300 hover:text-amber-100 transition-colors"
                                                                        title="Set as default layer"
                                                                    >
                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                                <button
                                                                    onClick={() => setEditingLayerName(layer.id)}
                                                                    className="p-1 text-amber-300 hover:text-white transition-colors"
                                                                    title="Rename layer"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                                    </svg>
                                                                </button>
                                                                {layers.length > 1 && (
                                                                    <button
                                                                        onClick={() => deleteLayer(layer.id)}
                                                                        className="p-1 text-amber-300 hover:text-red-400 transition-colors"
                                                                        title="Delete layer"
                                                                    >
                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Add new layer button (only for editors) */}
                                    {canEdit && layers.length < MAX_LAYERS && (
                                        <div className="border-t border-amber-700/50 p-2">
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    placeholder="New layer name..."
                                                    value={newLayerName}
                                                    onChange={(e) => setNewLayerName(e.target.value)}
                                                    className="flex-1 bg-amber-950 text-amber-100 px-3 py-1.5 rounded text-sm placeholder:text-amber-700 border border-amber-700"
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && newLayerName.trim()) {
                                                            createLayer(newLayerName.trim());
                                                        }
                                                    }}
                                                />
                                                <button
                                                    onClick={() => {
                                                        if (newLayerName.trim()) {
                                                            createLayer(newLayerName.trim());
                                                        }
                                                    }}
                                                    disabled={!newLayerName.trim()}
                                                    className="bg-amber-700 hover:bg-amber-600 disabled:bg-amber-900 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded text-sm transition-colors"
                                                >
                                                    Add
                                                </button>
                                            </div>
                                            <p className="text-amber-600 text-xs mt-1">
                                                {layers.length} / {MAX_LAYERS} layers
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Mobile: Compact tools toggle, Desktop: Full toolbar */}
                    {canEdit && (
                        <>
                            {/* Mobile tools toggle button */}
                            <button
                                onClick={() => setMobileToolsExpanded(!mobileToolsExpanded)}
                                className={`md:hidden ${woodPanelClass} p-2 text-amber-100 hover:text-white transition-colors flex items-center gap-2`}
                                style={woodPanelStyle}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                </svg>
                                <span className="text-sm">Tools</span>
                                <svg className={`w-4 h-4 transition-transform ${mobileToolsExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {/* Mobile expanded tools panel */}
                            {mobileToolsExpanded && (
                                <div
                                    className={`md:hidden ${woodPanelClass} p-3 flex flex-col gap-3 shadow-xl`}
                                    style={{
                                        background: 'linear-gradient(180deg, #6B4423 0%, #5D3A1A 50%, #4A2D14 100%)',
                                        border: '2px solid #C9A227',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                    }}
                                >
                                    {/* Palette toggle */}
                                    <button
                                        onClick={() => { setShowPalette(!showPalette); setMobileToolsExpanded(false); }}
                                        className={`w-full px-3 py-2 rounded-lg font-medium transition-colors text-sm flex items-center gap-2 ${
                                            showPalette
                                                ? 'bg-purple-700 text-white'
                                                : 'bg-amber-900/50 text-amber-100 hover:text-white hover:bg-amber-900/70'
                                        }`}
                                    >
                                        <span>🎨</span>
                                        <span>{showPalette ? 'Hide Palette' : 'Show Palette'}</span>
                                    </button>

                                    {/* Tool selector - 2x3 grid on mobile */}
                                    <div className="grid grid-cols-3 gap-1">
                                        <button
                                            onClick={() => { setPaintTool('brush'); setLineStart(null); }}
                                            className={`p-3 rounded transition-colors flex flex-col items-center gap-1 ${
                                                paintTool === 'brush'
                                                    ? 'bg-purple-700 text-white'
                                                    : 'bg-amber-900/50 text-amber-100 hover:text-white hover:bg-amber-900/70'
                                            }`}
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                            </svg>
                                            <span className="text-xs">Brush</span>
                                        </button>
                                        <button
                                            onClick={() => { setPaintTool('line'); setLineStart(null); }}
                                            className={`p-3 rounded transition-colors flex flex-col items-center gap-1 ${
                                                paintTool === 'line'
                                                    ? 'bg-purple-700 text-white'
                                                    : 'bg-amber-900/50 text-amber-100 hover:text-white hover:bg-amber-900/70'
                                            }`}
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 20L20 4" />
                                            </svg>
                                            <span className="text-xs">Line</span>
                                        </button>
                                        <button
                                            onClick={() => { setPaintTool('fill'); setLineStart(null); }}
                                            className={`p-3 rounded transition-colors flex flex-col items-center gap-1 ${
                                                paintTool === 'fill'
                                                    ? 'bg-purple-700 text-white'
                                                    : 'bg-amber-900/50 text-amber-100 hover:text-white hover:bg-amber-900/70'
                                            }`}
                                        >
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M16.56 8.94L7.62 0 6.21 1.41l2.38 2.38-5.15 5.15a1.49 1.49 0 000 2.12l5.5 5.5c.29.29.68.44 1.06.44s.77-.15 1.06-.44l5.5-5.5c.59-.58.59-1.53 0-2.12zM5.21 10L10 5.21 14.79 10H5.21zM19 11.5s-2 2.17-2 3.5c0 1.1.9 2 2 2s2-.9 2-2c0-1.33-2-3.5-2-3.5z" />
                                            </svg>
                                            <span className="text-xs">Fill</span>
                                        </button>
                                        <button
                                            onClick={() => { setPaintTool('erase'); setLineStart(null); }}
                                            className={`p-3 rounded transition-colors flex flex-col items-center gap-1 ${
                                                paintTool === 'erase'
                                                    ? 'bg-red-700 text-white'
                                                    : 'bg-amber-900/50 text-amber-100 hover:text-white hover:bg-amber-900/70'
                                            }`}
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            <span className="text-xs">Erase</span>
                                        </button>
                                        <button
                                            onClick={() => { setPaintTool('lore'); setLineStart(null); setSelectedTile(null); }}
                                            className={`p-3 rounded transition-colors flex flex-col items-center gap-1 ${
                                                paintTool === 'lore'
                                                    ? 'bg-amber-700 text-white'
                                                    : 'bg-amber-900/50 text-amber-100 hover:text-white hover:bg-amber-900/70'
                                            }`}
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                            </svg>
                                            <span className="text-xs">Lore</span>
                                        </button>
                                    </div>

                                    {/* Brush size selector (shown for brush and erase tools) */}
                                    {(paintTool === 'brush' || paintTool === 'erase') && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-amber-300 text-xs">Size:</span>
                                            <div className="flex gap-1 flex-1">
                                                {[1, 2, 3, 4, 5].map((size) => (
                                                    <button
                                                        key={size}
                                                        onClick={() => setBrushSize(size)}
                                                        className={`flex-1 h-8 rounded text-sm font-medium transition-colors ${
                                                            brushSize === size
                                                                ? (paintTool === 'erase' ? 'bg-red-700 text-white' : 'bg-purple-700 text-white')
                                                                : 'bg-amber-900/50 text-amber-100 hover:text-white hover:bg-amber-900/70'
                                                        }`}
                                                    >
                                                        {size}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Desktop tools - horizontal layout */}
                            <div className="hidden md:flex gap-2">
                                <button
                                    onClick={() => setShowPalette(!showPalette)}
                                    className={`${woodPanelClass} px-4 py-2 font-medium transition-colors ${
                                        showPalette
                                            ? 'bg-purple-700 text-white'
                                            : 'text-amber-100 hover:text-white'
                                    }`}
                                    style={showPalette ? undefined : woodPanelStyle}
                                >
                                    🎨 {showPalette ? 'Hide Palette' : 'Show Palette'}
                                </button>

                                {/* Tool selector */}
                                <div className={`${woodPanelClass} p-1 flex gap-1`} style={woodPanelStyle}>
                                    <button
                                        onClick={() => { setPaintTool('brush'); setLineStart(null); }}
                                        className={`${woodButtonClass} ${
                                            paintTool === 'brush'
                                                ? toolActiveColors.brush
                                                : woodButtonInactiveClass
                                        }`}
                                        title="Brush Tool - Click or drag to paint"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => { setPaintTool('line'); setLineStart(null); }}
                                        className={`${woodButtonClass} ${
                                            paintTool === 'line'
                                                ? toolActiveColors.line
                                                : woodButtonInactiveClass
                                        }`}
                                        title="Line Tool - Click and drag to draw a line"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 20L20 4" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => { setPaintTool('fill'); setLineStart(null); }}
                                        className={`${woodButtonClass} ${
                                            paintTool === 'fill'
                                                ? toolActiveColors.fill
                                                : woodButtonInactiveClass
                                        }`}
                                        title="Fill Tool - Click to fill connected empty tiles"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M19 11h-6V5h-2v6H5v2h6v6h2v-6h6v-2z" opacity="0" />
                                            <path d="M16.56 8.94L7.62 0 6.21 1.41l2.38 2.38-5.15 5.15a1.49 1.49 0 000 2.12l5.5 5.5c.29.29.68.44 1.06.44s.77-.15 1.06-.44l5.5-5.5c.59-.58.59-1.53 0-2.12zM5.21 10L10 5.21 14.79 10H5.21zM19 11.5s-2 2.17-2 3.5c0 1.1.9 2 2 2s2-.9 2-2c0-1.33-2-3.5-2-3.5z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => { setPaintTool('erase'); setLineStart(null); }}
                                        className={`${woodButtonClass} ${
                                            paintTool === 'erase'
                                                ? toolActiveColors.erase
                                                : woodButtonInactiveClass
                                        }`}
                                        title="Erase Tool - Click or drag to remove tiles"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => { setPaintTool('lore'); setLineStart(null); setSelectedTile(null); }}
                                        className={`${woodButtonClass} ${
                                            paintTool === 'lore'
                                                ? toolActiveColors.lore
                                                : woodButtonInactiveClass
                                        }`}
                                        title="Lore Tool - Click to add/view lore"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Brush size selector (shown for brush and erase tools) */}
                                {(paintTool === 'brush' || paintTool === 'erase') && (
                                    <div className={`${woodPanelClass} p-1 flex gap-1 items-center`} style={woodPanelStyle}>
                                        <span className="text-amber-300 text-xs px-1">Size:</span>
                                        {[1, 2, 3, 4, 5].map((size) => (
                                            <button
                                                key={size}
                                                onClick={() => setBrushSize(size)}
                                                className={`w-7 h-7 rounded text-sm font-medium transition-colors ${
                                                    brushSize === size
                                                        ? (paintTool === 'erase' ? 'bg-red-700 text-white' : 'bg-purple-700 text-white')
                                                        : 'text-amber-100 hover:text-white hover:bg-amber-900/50'
                                                }`}
                                                title={`${paintTool === 'erase' ? 'Erase' : 'Brush'} size ${size}${size === 1 ? ' (single hex)' : ` (${size} rings)`}`}
                                            >
                                                {size}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Selected tile indicator - hidden on mobile when tools expanded */}
                {canEdit && selectedTile && paintTool !== 'erase' && (
                    <div
                        className={`absolute bottom-4 left-4 ${woodPanelClass} p-2 flex items-center gap-2 z-10 ${mobileToolsExpanded ? 'hidden md:flex' : ''}`}
                        style={woodPanelStyle}
                    >
                        <img src={selectedTile.url} alt="Selected tile" className="w-10 h-10 rounded border border-amber-600" />
                        <span className="text-amber-100 text-sm hidden md:inline">
                            {paintTool === 'brush' && 'Click or drag to paint'}
                            {paintTool === 'line' && 'Click and drag to draw a line'}
                            {paintTool === 'fill' && 'Click to fill empty area'}
                        </span>
                        <button
                            onClick={() => setSelectedTile(null)}
                            className="text-amber-300 hover:text-white ml-2"
                            title="Clear selection"
                        >
                            ✕
                        </button>
                    </div>
                )}

                {/* Erase tool indicator - hidden on mobile when tools expanded */}
                {canEdit && paintTool === 'erase' && (
                    <div
                        className={`absolute bottom-4 left-4 ${woodPanelClass} p-2 flex items-center gap-2 z-10 ${mobileToolsExpanded ? 'hidden md:flex' : ''}`}
                        style={woodPanelStyle}
                    >
                        <div className="w-10 h-10 rounded bg-red-600/30 flex items-center justify-center border border-red-500">
                            <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </div>
                        <span className="text-amber-100 text-sm hidden md:inline">Click or drag to erase tiles</span>
                    </div>
                )}

                {/* Lore tool indicator - hidden on mobile when tools expanded */}
                {canEdit && paintTool === 'lore' && (
                    <div
                        className={`absolute bottom-4 left-4 ${woodPanelClass} p-2 flex items-center gap-2 z-10 ${mobileToolsExpanded ? 'hidden md:flex' : ''}`}
                        style={woodPanelStyle}
                    >
                        <div className="w-10 h-10 rounded bg-amber-600/30 flex items-center justify-center border border-amber-500">
                            <svg className="w-6 h-6 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                        </div>
                        <span className="text-amber-100 text-sm hidden md:inline">Click a hex to add or view lore</span>
                    </div>
                )}

                {/* Coordinate display */}
                {hoveredCoord && (
                    <div
                        className={`absolute bottom-4 right-4 ${woodPanelClass} px-3 py-1.5 text-amber-100 text-sm font-mono z-10`}
                        style={woodPanelStyle}
                    >
                        ({hoveredCoord.q}, {hoveredCoord.r})
                    </div>
                )}
            </div>

            {/* Tile Palette */}
            {canEdit && showPalette && (
                <TilePalette
                    selectedTile={selectedTile}
                    onSelectTile={setSelectedTile}
                    onClose={() => setShowPalette(false)}
                />
            )}

            {/* Lore Modal */}
            {loreModalCoord && (
                <LoreModal
                    isOpen={showLoreModal}
                    onClose={() => {
                        setShowLoreModal(false);
                        setLoreModalCoord(null);
                        setLoreModalContent(null);
                    }}
                    canEdit={canEdit}
                    loreEntry={currentLoreEntry}
                    loreContent={loreModalContent}
                    isLoading={loreModalLoading}
                    coordinates={loreModalCoord}
                    onSave={handleLoreSave}
                    onDelete={handleLoreDelete}
                />
            )}
        </div>
    );
}

